import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import ProjectDetail from "./pages/ProjectDetail";
import HouseholdDetail from "./pages/HouseholdDetail";
import TeamManagement from "./pages/TeamManagement";
import Integrations from "./pages/Integrations";
import DocusignTrialCallback from "./pages/DocusignTrialCallback";
import ClientPortal from "./pages/ClientPortal";
import DocumentList from "./pages/DocumentList";
import ClientDocumentList from "./pages/ClientDocumentList";
import TransitionTracker from "./pages/TransitionTracker";
import ProjectInsights from "./pages/ProjectInsights";
import { checkAuth } from "./api/auth";
import { NotificationProvider } from "./context/NotificationContext";
import { ToastProvider } from "./context/ToastContext";
import { ConfirmProvider } from "./context/ConfirmContext";
import { ProjectMembersProvider } from "./context/ProjectMembersContext";

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    // Skip auth check for client portal routes
    if (location.pathname.startsWith('/household/')) {
      setAuthLoading(false);
      return;
    }

    const verifyAuth = async () => {
      const authResult = await checkAuth();
      setIsAuthenticated(authResult.authenticated);
      setUser(authResult.user);
      setAuthLoading(false);
    };
    
    verifyAuth();
  }, [location.pathname]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Login setIsAuthenticated={setIsAuthenticated} setUser={setUser} />
          )
        }
      />
      <Route
        path="/dashboard"
        element={
          isAuthenticated ? (
            <Dashboard user={user} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/project/:projectId"
        element={
          isAuthenticated ? (
            <ProjectDetail user={user} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/project/:projectId/house/:householdId"
        element={
          isAuthenticated ? (
            <HouseholdDetail user={user} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/project/:projectId/transition-tracker"
        element={
          isAuthenticated ? (
            <TransitionTracker user={user} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/project/:projectId/insights"
        element={
          isAuthenticated ? (
            <ProjectInsights user={user} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/project/:projectId/house/:householdId/documents"
        element={
          isAuthenticated ? (
            <DocumentList user={user} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/teams"
        element={
          isAuthenticated ? (
            <TeamManagement user={user} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/integrations"
        element={
          isAuthenticated ? (
            <Integrations user={user} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/integrations/docusign-trial-callback"
        element={
          isAuthenticated ? (
            <DocusignTrialCallback />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/household/:household_id/documents"
        element={<ClientDocumentList />}
      />
      <Route
        path="/household/:household_id/*"
        element={<ClientPortal />}
      />
      <Route path="/" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <NotificationProvider>
        <ToastProvider>
          <ConfirmProvider>
            <ProjectMembersProvider>
              <AppContent />
            </ProjectMembersProvider>
          </ConfirmProvider>
        </ToastProvider>
      </NotificationProvider>
    </Router>
  );
}

export default App;

